import React, { useEffect } from "react";
import logo from "../images/quizzy_logo.png";

// EXTERNAL PACKAGES
import { nanoid } from "nanoid";
// This package is needed to decode the html entities of inside the questions gathered from the API
import { decode } from "html-entities";

export default function Quiz(props) {
  // Using UseEffect to add the event listener to the window for the scroll event (to make the back-to-top button appear and disappear) only when the QUESTIONS SCREEN is rendered (props.questions.length >= 5). To achieve this we use as target the length of the 'questions' state array received as props. With the if condition inside the effect function we add the event listener to the window only when the questions state array passed as props will contain > 5 questions (props.questions.length >= 5), meaning that the event listener will be added to the window only after the QUESTIONS SREEN is rendered
  // The effect function will run everytime props.questions.length changes, but for the LOADING SCREEN and NO QUESTION SCREEN props.questions.length will be less than 5, so the effect function will not produce any effect because of the internal if condition
  // We also remove the event listener when the <Quiz /> component gets unmounted, to avoid memory leaks
  useEffect(() => {
    if (props.questions.length >= 5) {
      const backToTopBtn = document.querySelector(".back-to-top");

      function checkScrollTop() {
        // console.log("scrolling...");

        if (
          document.body.scrollTop > 20 ||
          document.documentElement.scrollTop > 20
        ) {
          backToTopBtn.classList.add("back-to-top--visible");
        } else {
          backToTopBtn.classList.remove("back-to-top--visible");
        }
      }

      window.addEventListener("scroll", checkScrollTop);

      // Returning a function to clear the effect when the <Quiz/> component gets unmounted
      return function clear() {
        // console.log("event listener removed");
        window.removeEventListener("scroll", checkScrollTop);
      };
    }
  }, [props.questions.length]);

  // LOOPING ON THE QUESTIONS STATE-ARRAY (RECEIVED AS PROP) AND GENERATE A DIV FOR EACH QUESTION
  // Each .question div will output in the UI the category of the question, the title of the question and all the answers, rendered in random order and each one in its own div (div.answer)
  const questionsElements =
    props.questions.length >= 5 &&
    props.questions.map(question => {
      return (
        <div
          key={nanoid()}
          className={`question question--${question.difficulty} ${
            props.quizComplete &&
            question.userAnswer !== question.correct_answer
              ? "question--wrong"
              : ""
          }`}
        >
          <h4 className="question__category">{question.category}</h4>
          <h2 className="question__title">{decode(question.question)}</h2>
          <div className="answers">
            {
              // Looping on the question.allAnswers array to generate an array of div.answer jsx elements, one for each answer inside the question.allAnswers array, that will render in the UI all the answer divs
              question.allAnswers.map(answer => {
                return (
                  <div
                    key={nanoid()}
                    className={`answer ${
                      answer === question.userAnswer ? "userAnswer" : ""
                    } ${
                      props.quizComplete && answer === question.correct_answer
                        ? "correct"
                        : ""
                    } ${
                      props.quizComplete &&
                      answer === question.userAnswer &&
                      question.userAnswer !== question.correct_answer
                        ? "wrong"
                        : ""
                    } ${
                      props.quizComplete &&
                      answer !== question.userAnswer &&
                      answer !== question.correct_answer
                        ? "faded"
                        : ""
                    }`}
                    onClick={() => props.addUserAnswer(answer, question.id)}
                  >
                    {decode(answer)}
                  </div>
                );
              })
            }
          </div>
        </div>
      );
    });

  // calculating the number of correct answers after the user clicked on the .checkAnswer button
  const correctAnswersCount =
    props.quizComplete &&
    props.questions.filter(
      question => question.correct_answer === question.userAnswer
    ).length;

  return (
    <>
      <div className="top-bar">
        <img src={logo} className="logo" alt="Quizzy logo" />
        <div
          className="back-to-top"
          onClick={() => document.body.scrollIntoView({ behavior: "smooth" })}
          title="Back to the top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            className="back-to-top-trigger"
            viewBox="0 0 16 16"
          >
            <path d="M2 16a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2zm6.5-4.5V5.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 5.707V11.5a.5.5 0 0 0 1 0z" />
          </svg>{" "}
        </div>
      </div>
      {
        // SPINNER SCREEN
        // this will show up when the questions array is empty (this will happen right after the user starts the quiz. Indeed, after the user starts the quiz (submit the form), the gameStart state will be set to true causing a ri-render. After this ri-render the <quiz> component will be rendered instead of <intro>, and the questions state array is passed to this component as PROP, but at this stage the questions prop is still empty [] (props.questions.length === 0)). The effect function (that will run because his dependency, gameStart, has changed) that will set the 'questions' state to hold the fetched data, will indeed run ONLY AFTER THE RENDER, so this is the reason why the UI, after the user Starts the quiz, we will see the spinner screen for few milliseconds and then the questions screen (or the no-questions screen)
        props.questions.length === 0 && (
          <div className="loading-quiz">
            <h2>Loading your Quiz...</h2>
            <div className="lds-hourglass"></div>
          </div>
        )
      }
      {
        // NO-QUESTIONS SCREEN
        // This will show up only if the combination of options of the user will not produce any result for the fetch. In that case the questions state-array will contain only one element (a string) and not the question objects
        props.questions.length === 1 && (
          <div className="no-questions">
            <div className="no-questions__inner">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                className="no-questions__icon"
                viewBox="0 0 16 16"
              >
                <path d="M6.95.435c.58-.58 1.52-.58 2.1 0l6.515 6.516c.58.58.58 1.519 0 2.098L9.05 15.565c-.58.58-1.519.58-2.098 0L.435 9.05a1.482 1.482 0 0 1 0-2.098L6.95.435zm1.4.7a.495.495 0 0 0-.7 0L1.134 7.65a.495.495 0 0 0 0 .7l6.516 6.516a.495.495 0 0 0 .7 0l6.516-6.516a.495.495 0 0 0 0-.7L8.35 1.134z" />
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
              </svg>
              <h2 className="no-questions__text">{props.questions[0]}</h2>
              <button className="no-questions__btn" onClick={props.rebuildQuiz}>
                Try Again!
              </button>
            </div>
          </div>
        )
      }
      {
        // QUESTIONS SCREEN
        props.questions.length >= 5 && (
          <div className="questions">
            <div className="container">
              <div className="questions-info">
                <div className="questions-info__label questions-info__label--easy">
                  easy
                </div>
                <div className="questions-info__label questions-info__label--medium">
                  medium
                </div>
                <div className="questions-info__label questions-info__label--hard">
                  hard
                </div>
              </div>
              <div className="questions-items">{questionsElements}</div>
            </div>
          </div>
        )
      }
      <div className="bottom-bar">
        {props.questions.length >= 5 &&
          (props.quizComplete ? (
            <div className="questions-results">
              <div className="user-score">
                {correctAnswersCount >= props.questions.length / 2
                  ? `😃 `
                  : "😕 "}
                You scored {correctAnswersCount} / {props.questions.length}{" "}
                correct answers
              </div>
              <button className="playAgain" onClick={props.playAgain}>
                Play Again!
              </button>
            </div>
          ) : (
            <div className="questions-check">
              <div className="user-answers-count">
                Answers:{" "}
                {props.questions.filter(question => question.userAnswer).length}{" "}
                / {props.questions.length}
              </div>
              <button className="checkAnswers" onClick={props.checkAnswers}>
                Check Answers
              </button>
            </div>
          ))}
        <div className="copyright">
          &copy; 2022{" "}
          <a
            className="copyright__link"
            href="https://www.linkedin.com/in/vincent-fattore/"
            target="_blank"
          >
            Fattore Web Design
          </a>
        </div>
      </div>
    </>
  );
}
